import { useState, useEffect } from 'react'
import { api } from '../../api/client'
import { CreditCard, RefreshCw, Smartphone } from 'lucide-react'
import ErrorBanner from '../../components/ErrorBanner'
import { StatusBadge } from '../../components/PageParts'
import { SettingsHeader, SettingsTabs } from './settingsShared'

export default function PaymentsSettings() {
  const [loading, setLoading] = useState(false)
  const [stripeStatus, setStripeStatus] = useState(null)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    checkStripeStatus()

    // Check if returning from Stripe onboarding
    const params = new URLSearchParams(window.location.search)
    if (params.get('stripe') === 'return') {
      setRefreshing(true)
      setTimeout(() => {
        checkStripeStatus()
        setRefreshing(false)
        // Clean up URL
        window.history.replaceState({}, '', '/settings/payments')
      }, 2000)
    }
  }, [])

  const checkStripeStatus = async () => {
    try {
      const res = await api.get('/stripe/connect/status')
      setStripeStatus(res.data)
    } catch (e) {
      console.error('Failed to check Stripe status:', e)
      setError(e.message)
    }
  }

  const connectStripe = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.post('/stripe/connect/onboard', {})
      const { url } = res.data || {}
      if (!url) throw new Error('Failed to start onboarding')

      // Redirect to Stripe onboarding
      window.location.href = url

    } catch (e) {
      setError(e.message)
      setLoading(false)
    }
  }

  const disconnectStripe = async () => {
    setError('')
    const typed = window.prompt(
      'Type DISCONNECT to disconnect Stripe. Card payments in store will stop.',
      ''
    )
    if (typed === null) {
      return
    }
    if (String(typed).trim().toUpperCase() !== 'DISCONNECT') {
      setError('Stripe is still connected: type DISCONNECT exactly to disconnect it.')
      return
    }

    setLoading(true)
    try {
      await api.post('/stripe/connect/disconnect', { confirmation: 'DISCONNECT' })
      await checkStripeStatus()
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  const status = !stripeStatus
    ? null
    : !stripeStatus.connected
      ? { label: 'Not connected', tone: 'muted' }
      : stripeStatus.enabled
        ? { label: 'Card payments on', tone: 'good' }
        : { label: 'Finish setting up', tone: 'warn' }

  return (
    <div className="ci-page">
      <SettingsTabs />
      <SettingsHeader title="Payments" meta="How customers can pay in store, besides cash." />
      {refreshing && <p className="mf-notice"><RefreshCw size={14} />Checking your Stripe account after setup…</p>}
      {error && <ErrorBanner message={error} />}

      <div className="set-stack">
        <div className="ci-form-meta-card mf-form">
          <div className="mf-card-title set-card-title">
            <CreditCard size={16} />Card payments (Stripe Terminal)
            {status && <StatusBadge label={status.label} tone={status.tone} />}
          </div>
          <div className="set-body">
            <p>Take credit and debit cards in store with a Stripe card reader. The money goes straight to your bank account. Stripe’s card fees apply; ASYS adds no setup or monthly fees.</p>
            {stripeStatus?.connected && <p className="doc-hint">Stripe account {stripeStatus.account_id}{!stripeStatus.enabled && ' · finish the Stripe setup to start taking cards.'}</p>}
            <p className="doc-hint">
              Works with Stripe’s Bluetooth reader on iPads, Android tablets and phones.{' '}
              <a href="https://stripe.com/terminal" target="_blank" rel="noopener noreferrer">See the card readers</a>
            </p>
            <div className="set-actions">
              {stripeStatus?.connected ? (
                <>
                  {!stripeStatus.enabled && (
                    <button data-write type="button" className="ci-btn ci-btn-primary" onClick={connectStripe} disabled={loading}>{loading ? 'Opening Stripe…' : 'Finish setting up'}</button>
                  )}
                  <button data-write type="button" className="ci-btn ci-btn-ghost ci-btn-danger" onClick={disconnectStripe} disabled={loading}>Disconnect Stripe</button>
                </>
              ) : (
                <button data-write type="button" className="ci-btn ci-btn-primary" onClick={connectStripe} disabled={loading}>{loading ? 'Opening Stripe…' : 'Connect Stripe'}</button>
              )}
            </div>
          </div>
        </div>

        <div className="ci-form-meta-card mf-form">
          <div className="mf-card-title set-card-title">
            <Smartphone size={16} />USSD payments (Flutterwave)
            <StatusBadge label="On · Nigeria" tone="good" />
          </div>
          <div className="set-body">
            <p>Customers pay by dialling a USSD code on their phone, so no card reader is needed.</p>
            <p className="doc-hint">Set up on the ASYS server. Contact ASYS support to change it.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
